import { Dispatch } from "react";

import { OrganisationPermissionConstants, OrganisationTypeConstants } from "../../../../../../constants";
import { ActionButtons, Column, ErrorBox, KanaTooltip, Row, TextInput } from "../../../../../../widget";
import { OrganisationDetailsData, OrganisationFormChangedAction, UpdateDetailsType } from "../../models";
import { useDetailsForm } from "./useDetailsForm";

interface DetailsFormProps {
  objectUuid: string;
  objectKey: string;
  updateDetails: UpdateDetailsType;
  organisationDefaultDetails: OrganisationDetailsData;
  onDetailsUpdated: (details: OrganisationDetailsData) => void;
  dispatch: Dispatch<OrganisationFormChangedAction>;
}

export const DetailsForm = ({
  objectUuid,
  objectKey,
  updateDetails,
  organisationDefaultDetails,
  onDetailsUpdated,
  dispatch,
}: DetailsFormProps): JSX.Element => {
  const {
    displayName,
    contactEmail,
    phoneNumber,
    website,
    linkedinURL,
    twitterUsername,
    instagramUsername,
    youtubeChannel,
    facebookURL,
    errors,
    isHandleSubmitLoading,
    currentUserType,
    setDisplayName,
    setPhoneNumber,
    setContactEmail,
    setWebsite,
    setLinkedinURL,
    setTwitterUsername,
    setInstagramUsername,
    setYoutubeChannel,
    setFacebookURL,
    handleSubmit,
    handleCancel,
    hasPermission,
  } = useDetailsForm(objectUuid, objectKey, updateDetails, organisationDefaultDetails, onDetailsUpdated, dispatch);
  const canEditOrganisation = hasPermission(OrganisationPermissionConstants.WRITE_ORGANISATION);

  return (
    <>
      {errors && errors.length > 0 && (
        <Row spacingV="ll">
          <Column span={5}>
            <ErrorBox title="Please solve the below issues before saving" text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}
      <form onSubmit={(e) => handleSubmit(e)}>
        <Row spacingV="ll">
          <Column span={5}>
            <TextInput
              label="Display name"
              name="displayName"
              tooltip={
                currentUserType === OrganisationTypeConstants.DEVELOPER && (
                  <KanaTooltip
                    tooltipHeader="What is a display name?"
                    tooltipText="This controls the name of your business that will be displayed to the public in the directory, on the project search screen, the project overviews and on your developer profile."
                  />
                )
              }
              value={displayName}
              disabled={!canEditOrganisation}
              onChange={(value) => setDisplayName(value)}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={5}>
            <TextInput
              label="Phone number"
              name="phoneNumber"
              value={phoneNumber}
              disabled={!canEditOrganisation}
              onChange={(value) => setPhoneNumber(value)}
            />
          </Column>
          <Column span={5} offset={1}>
            <TextInput
              label="Contact email address"
              name="contactEmailAddress"
              type="email"
              tooltip={
                currentUserType === OrganisationTypeConstants.DEVELOPER && (
                  <KanaTooltip
                    tooltipHeader="What will we use your contact email address for?"
                    tooltipText="This is where we will send any incoming contact requests, and how we will inform you when people register interest in your projects."
                  />
                )
              }
              value={contactEmail}
              disabled={!canEditOrganisation}
              onChange={(value) => setContactEmail(value)}
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={5}>
            <TextInput
              optional
              label="Website"
              name="website"
              value={website}
              disabled={!canEditOrganisation}
              onChange={(value) => setWebsite(value)}
            />
          </Column>
          <Column span={5} offset={1}>
            <TextInput
              optional
              label="LinkedIn URL"
              name="linkedinURL"
              value={linkedinURL}
              disabled={!canEditOrganisation}
              onChange={(value) => setLinkedinURL(value)}
              tooltip={
                <KanaTooltip
                  tooltipHeader="What format should you use?"
                  tooltipText="Please provide your Linkedin URL in the following format:
                  https://www.linkedin.com/company/ kana-earth/"
                />
              }
            />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={5}>
            <TextInput
              optional
              label="Twitter username"
              name="twitterUsername"
              value={twitterUsername}
              disabled={!canEditOrganisation}
              onChange={(value) => setTwitterUsername(value)}
              tooltip={
                <KanaTooltip
                  tooltipHeader="What format should you use?"
                  tooltipText="Please provide your Twitter username with no @ symbol or URL, for example:
                  getkana"
                />
              }
            />
          </Column>
          <Column span={5} offset={1}>
            <TextInput
              optional
              label="Instagram username"
              name="instagramUsername"
              value={instagramUsername}
              disabled={!canEditOrganisation}
              onChange={(value) => setInstagramUsername(value)}
              tooltip={
                <KanaTooltip
                  tooltipHeader="What format should you use?"
                  tooltipText="Please provide your Instagram username with no @ symbol or URL, for example:
                  getkanaearth"
                />
              }
            />
          </Column>
        </Row>
        <Row spacingV="2xl">
          <Column span={5}>
            <TextInput
              optional
              label="Youtube Channel"
              name="youtubeChannel"
              value={youtubeChannel}
              disabled={!canEditOrganisation}
              onChange={(value) => setYoutubeChannel(value)}
              tooltip={
                <KanaTooltip
                  tooltipHeader="What format should you use?"
                  tooltipText="Please provide your YouTube Channel in the following format:
                  https://www.youtube.com/channel/ getkanaearth or youtube.com/@yourhandle"
                />
              }
            />
          </Column>
          <Column span={5} offset={1}>
            <TextInput
              optional
              label="Facebook URL"
              name="facebookURL"
              value={facebookURL}
              disabled={!canEditOrganisation}
              onChange={(value) => setFacebookURL(value)}
              tooltip={
                <KanaTooltip
                  tooltipHeader="What format should you use?"
                  tooltipText="Please provide your Facebook URL in the following format:
                  https://www.facebook.com/ getkanaearth/"
                />
              }
            />
          </Column>
        </Row>
        {canEditOrganisation && (
          <Row spacingV={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? undefined : "2xl"}>
            <Column span={5}>
              <ActionButtons
                onCancel={handleCancel}
                isLoading={isHandleSubmitLoading}
                corners={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "rounded" : "default"}
              />
            </Column>
          </Row>
        )}
      </form>
    </>
  );
};
